import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { FiArrowLeft, FiNavigation, FiSearch, FiCheckCircle } from 'react-icons/fi';
import { FaApple, FaGooglePlay, FaInstagram, FaGlobe } from 'react-icons/fa';
import { GiPlantSeed, GiFertilizerBag, GiPlantRoots, GiFarmTractor, GiPestleMortar } from 'react-icons/gi';
import './AppComingSoon.css';

export default function AppComingSoon({ platform, onBack }) {
  const isIos = platform === 'ios';

  // Refs for entrance animations
  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const titleBoxRef = useRef(null);
  const phoneRef = useRef(null);
  const actionsRef = useRef(null);
  const backgroundLettersRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const tl = gsap.timeline({ defaults: { ease: 'power4.out', duration: 1 } });

    // Entrance Animations
    tl.fromTo(backgroundLettersRef.current.children,
      { opacity: 0, scale: 0.8, filter: 'blur(20px)' },
      { opacity: 0.1, scale: 1, filter: 'blur(8px)', stagger: 0.1, duration: 1.5 }
    )
      .fromTo(logoRef.current, { y: -30, opacity: 0 }, { y: 0, opacity: 1 }, '-=1')
      .fromTo(titleBoxRef.current.querySelector('.slogan-badge'), { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1 }, '-=0.8')
      .fromTo(titleBoxRef.current.querySelector('.app-title-main'), { y: 30, opacity: 0 }, { y: 0, opacity: 1 }, '-=0.6');

    const boxLetters = titleBoxRef.current.querySelectorAll('.coming-soon-box');
    if (boxLetters.length > 0) {
      tl.fromTo(boxLetters, { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.8 }, '-=0.4');
    }

    tl.fromTo(phoneRef.current, 
      { x: 100, y: 30, opacity: 0, rotationX: 0, rotationY: -45, rotationZ: 0 }, 
      { x: 0, y: 0, opacity: 1, rotationX: 12, rotationY: -22, rotationZ: 3, duration: 1.5, ease: 'power3.out' }, 
      '-=1'
    )
      .fromTo(actionsRef.current.children,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.15 },
        '-=0.8'
      );

    // Continuous float animation for the phone
    gsap.to(phoneRef.current, {
      y: -10,
      duration: 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: 1.5
    });

  }, []);

  return (
    <div
      className="app-coming-soon-container"
      ref={containerRef}


    >
      {/* Blurred Decorative Ambient Glow Blobs */}
      <div className="ambient-glow glow-primary"></div>
      <div className="ambient-glow glow-accent"></div>
      <div className="ambient-glow glow-secondary"></div>

      {/* Floating Letters in background for 3D depth of field */}
      <div className="bg-floating-letters" ref={backgroundLettersRef}>
        <div className="bg-letter letter-o" style={{ top: '15%', left: '10%' }}>O</div>
        <div className="bg-letter letter-r" style={{ top: '25%', right: '15%' }}>R</div>
        <div className="bg-letter letter-b" style={{ bottom: '15%', left: '20%' }}>B</div>
        <div className="bg-letter letter-i" style={{ bottom: '30%', right: '8%' }}>I</div>
        <div className="bg-letter letter-f" style={{ top: '50%', left: '5%' }}>F</div>
        <div className="bg-letter letter-e" style={{ bottom: '45%', right: '25%' }}>E</div>
      </div>

      {/* Vertical rotated high-tech tag */}
      <div className="vertical-agri-tag">
        ORBIFERT SMART ECOSYSTEM
      </div>

      <div className="container app-coming-soon-content">

        {/* LEFT COLUMN: Texts and CTA Actions */}
        <div className="app-content-left-panel">
          {/* Back button */}
          <button className="app-back-link" onClick={onBack}>
            <FiArrowLeft className="back-icon" /> Back to Shop
          </button>

          {/* Brand Logo & Name */}
          <div className="app-header-logo-wrap" ref={logoRef}>
            <img src="/logo/Orbifert Logo.png" alt="Orbifert Logo" className="app-brand-logo" />
            <div className="app-brand-details">
              <h1 className="app-brand-title">ORBIFERT</h1>
              <span className="app-brand-subtitle">ORGANIC PVT. LTD.</span>
            </div>
          </div>

          {/* Main Title Box */}
          <div className="title-box-wrapper" ref={titleBoxRef}>
            <div className="slogan-badge">
              <span className="badge-pulse-dot"></span>
              NEXT GEN DIGITAL FARMING
            </div>

            <h2 className="app-title-main">Our Smart Mobile App is</h2>

            {/* High-tech Coming Soon Box */}
            <div className="coming-soon-box">
              <span className="box-corner top-left"></span>
              <span className="box-corner top-right"></span>
              <span className="box-corner bottom-left"></span>
              <span className="box-corner bottom-right"></span>

              <span className="coming-soon-text">Coming Soon</span>

              {/* High-tech glow arrow pointer */}
              <div className="glowing-arrow-pointer">
                <FiNavigation className="pointer-arrow-svg" />
              </div>
            </div>
          </div>

          {/* Download Platforms Selector */}
          <div className="download-actions-wrapper" ref={actionsRef}>
            <p className="download-helper-text">Select your platform to register for early beta access:</p>

            <div className="platform-buttons-row">
              {/* App Store iOS Button */}
              <div className={`platform-action-card ${isIos ? 'active-platform' : 'inactive-platform'}`}>
                <div className="platform-card-icon-wrap">
                  <FaApple className="platform-icon ios-icon" />
                </div>
                <div className="platform-card-details">
                  <span className="platform-card-subtitle">Download for</span>
                  <h4 className="platform-card-title">Apple iOS</h4>
                </div>
                <span className="platform-status-badge">Beta Queued</span>
              </div>

              {/* Google Play Android Button */}
              <div className={`platform-action-card ${!isIos ? 'active-platform' : 'inactive-platform'}`}>
                <div className="platform-card-icon-wrap">
                  <FaGooglePlay className="platform-icon android-icon" />
                </div>
                <div className="platform-card-details">
                  <span className="platform-card-subtitle">Download for</span>
                  <h4 className="platform-card-title">Android APK</h4>
                </div>
                <span className="platform-status-badge">Beta Queued</span>
              </div>
            </div>

            {/* Newsletter input inside the panel */}
            <div className="app-newsletter-form">
              <input type="email" placeholder="Enter email for app launch invite..." className="app-newsletter-input" />
              <button className="app-newsletter-btn">Notify Me</button>
            </div>
          </div>

          {/* Footer Socials */}
          <div className="app-coming-soon-footer">
            <a href="https://www.orbifertorganic.com" target="_blank" rel="noreferrer" className="footer-link">
              <FaGlobe /> www.orbifertorganic.com
            </a>
            <span className="footer-divider">|</span>
            <a href="https://instagram.com/orbifert_organic" target="_blank" rel="noreferrer" className="footer-link">
              <FaInstagram /> @orbifert_organic
            </a>
          </div>
        </div>

        {/* RIGHT COLUMN: 3D Smartphone Mockup */}
        <div className="app-mockup-right-panel">
          <div
            className="phone-3d-wrapper"
            ref={phoneRef}

          >
            {/* Phone Back Shadow / Neon Glow Backlight */}
            <div className="phone-backlight-glow"></div>

            {/* Physical side button notches */}
            <div className="phone-physical-button vol-up"></div>
            <div className="phone-physical-button vol-down"></div>
            <div className="phone-physical-button power"></div>

            {/* Phone Outer Glass Body */}
            <div className="phone-body-chassis">
              {/* Screen Display Bezel */}
              <div className="phone-inner-screen">

                {/* Dynamic Camera Island */}
                <div className="phone-camera-notch">
                  <span className="camera-lens"></span>
                  <span className="sensor-dot"></span>
                </div>

                {/* APP SYSTEM CONTENT DISPLAY */}
                <div className="app-mockup-dashboard">

                  {/* Status Bar */}
                  <div className="dashboard-status-bar">
                    <span className="status-time">09:41</span>
                    <div className="status-indicators">
                      <span className="indicator-icon">📶</span>
                      <span className="indicator-icon">🔋</span>
                    </div>
                  </div>

                  {/* App Navigation Header */}
                  <div className="app-dash-header">
                    <div className="app-dash-profile">
                      <div className="dash-avatar">👨‍🌾</div>
                      <div className="dash-welcome">
                        <span className="welcome-tag">Welcome back,</span>
                        <h4 className="welcome-user">Ramesh Patel</h4>
                      </div>
                    </div>
                    <button className="dash-search-btn">
                      <FiSearch />
                    </button>
                  </div>

                  {/* Dashboard Content Container */}
                  <div className="dashboard-scrollable-content">

                    {/* Live Weather & Irrigation Recommendation */}
                    <div className="dash-card weather-recommendation-card">
                      <div className="card-top">
                        <span className="weather-temp">31°C</span>
                        <span className="weather-city">Bhopal, MP</span>
                      </div>
                      <p className="weather-tip">Ideal moisture conditions. Apply <strong>Orbifert Plus</strong> fertilizer in the evening.</p>
                      <div className="weather-bg-glow"></div>
                    </div>

                    {/* Soil Health Stats widget */}
                    <div className="dash-card stats-widget-card">
                      <h5 className="card-section-title">Soil Diagnostics</h5>

                      <div className="soil-metrics-grid">
                        <div className="metric-box">
                          <span className="metric-label">PH Level</span>
                          <span className="metric-value green-text">6.8 pH</span>
                          <span className="metric-status">Excellent</span>
                        </div>
                        <div className="metric-box">
                          <span className="metric-label">Moisture</span>
                          <span className="metric-value orange-text">54%</span>
                          <span className="metric-status">Moderate</span>
                        </div>
                        <div className="metric-box">
                          <span className="metric-label">NPK Ratio</span>
                          <span className="metric-value green-text">Stable</span>
                          <span className="metric-status">Good</span>
                        </div>
                      </div>
                    </div>

                    {/* AI Pest and Disease Diagnostic Camera Box */}
                    <div className="dash-card pest-diagnostic-widget">
                      <div className="pest-scanner-header">
                        <span className="scanner-badge-live">Live Scan</span>
                        <h5 className="card-section-title">AI Pest Detection</h5>
                      </div>

                      <div className="scanner-camera-viewport">
                        {/* Target Scan Framing Box */}
                        <div className="scanner-tracking-frame">
                          <span className="track-corner tl"></span>
                          <span className="track-corner tr"></span>
                          <span className="track-corner bl"></span>
                          <span className="track-corner br"></span>

                          {/* Moving Scanning Laser Line */}
                          <div className="scanner-laser-line"></div>

                          {/* Mock Plant Leaf image */}
                          <div className="mock-scanner-leaf">🌱</div>
                        </div>

                        {/* Scanner overlay message */}
                        <div className="scanner-status-overlay">
                          <FiCheckCircle className="check-ok-icon" /> Safe & Healthy
                        </div>
                      </div>
                    </div>

                    {/* Premium Product Quick Add widget */}
                    <div className="dash-card products-quick-widget">
                      <h5 className="card-section-title">Best Sellers</h5>
                      <div className="quick-product-row">
                        <div className="q-prod-card">
                          <img src="/products/Super Novel.png" alt="Super Novel" className="q-prod-img" />
                          <span className="q-prod-name">Super Novel</span>
                          <span className="q-prod-price">₹170</span>
                        </div>
                        <div className="q-prod-card">
                          <img src="/products/Organic Manure.png" alt="Organic Manure" className="q-prod-img" />
                          <span className="q-prod-name">Organic Manure</span>
                          <span className="q-prod-price">₹120</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dashboard Bottom Navigation Bar Mockup */}
                  <div className="dashboard-bottom-nav">
                    <div className="nav-item active-nav-item">
                      <GiPlantSeed />
                      <span>Farm</span>
                    </div>
                    <div className="nav-item">
                      <GiFertilizerBag />
                      <span>Shop</span>
                    </div>
                    <div className="nav-item scanner-nav-center">
                      <div className="scanner-center-btn">
                        <GiPestleMortar />
                      </div>
                    </div>
                    <div className="nav-item">
                      <GiPlantRoots />
                      <span>Roots</span>
                    </div>
                    <div className="nav-item">
                      <GiFarmTractor />
                      <span>Tools</span>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
